import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { getUserProfileFull, getUserActivities, getUserChallenges } from '../api/users';
import { getAvatar } from '../api/avatars';
import { sendFriendRequest, respondFriendRequest, deleteFriend, blockFriend, deleteFriendRequest } from '../api/social';
import Avatar from '../components/Avatar';
import { MoreIcon, HotPotatoIcon } from '../assets/icons';
import { fetchDefaultAvatarSvg, formatActivityName } from '../utils';
import '../styles/UserProfilePage.css';

const prettyToken = (value) => String(value || '').replace(/_/g, ' ');

const challengeStatus = (ch) => {
    const status = ch.computed_status || ch.status;
    if (status === 'active') return { label: 'Active', tone: 'active' };
    if (status === 'upcoming') return { label: 'Upcoming', tone: 'upcoming' };
    if (status === 'completed') return { label: 'Completed', tone: 'completed' };
    return { label: prettyToken(status) || 'Unknown', tone: 'muted' };
};

const relationshipLabel = (status) => {
    if (status === 'self') return 'This is you';
    if (status === 'friend') return 'Friends';
    if (status === 'pending_sent') return 'Request sent';
    if (status === 'pending_received') return 'Sent you a friend request';
    return null;
};

const UserProfilePage = () => {
    const { username } = useParams();
    const history = useHistory();

    const [profile, setProfile] = useState(null);
    const [avatarSvg, setAvatarSvg] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [actionMessage, setActionMessage] = useState('');
    const [actionError, setActionError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const [activities, setActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [activitiesHasMore, setActivitiesHasMore] = useState(false);
    const [challenges, setChallenges] = useState([]);
    const [challengesLoading, setChallengesLoading] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, [username]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onPointerDown = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [menuOpen]);

    const fetchProfile = async () => {
        setLoading(true);
        setError(null);
        setActionMessage('');
        setActionError('');
        setConfirmAction(null);
        setMenuOpen(false);
        setActivities([]);
        setChallenges([]);
        try {
            const data = await getUserProfileFull(username);
            setProfile(data);

            if (data.avatar_id) {
                try {
                    const avatarData = await getAvatar(data.avatar_id);
                    setAvatarSvg(avatarData.avatar.svg_data);
                } catch {
                    setAvatarSvg(await fetchDefaultAvatarSvg());
                }
            } else {
                setAvatarSvg(await fetchDefaultAvatarSvg());
            }

            const isFriend = data.friendship_status === 'friend';
            const isSelf = data.friendship_status === 'self';
            if (isFriend || isSelf) {
                fetchActivities(data.username);
                fetchChallenges(data.username);
            }
        } catch (err) {
            setError(err.message || 'Failed to load user profile');
        } finally {
            setLoading(false);
        }
    };

    const fetchActivities = async (uname, offset = 0) => {
        setActivitiesLoading(true);
        try {
            const data = await getUserActivities(uname || username, offset, 20);
            if (offset === 0) {
                setActivities(data.activities || []);
            } else {
                setActivities(prev => [...prev, ...(data.activities || [])]);
            }
            setActivitiesHasMore(data.has_more || false);
        } catch {
            // Activity list is supplementary; the profile itself still renders.
        } finally {
            setActivitiesLoading(false);
        }
    };

    const fetchChallenges = async (uname) => {
        setChallengesLoading(true);
        try {
            const data = await getUserChallenges(uname || username);
            setChallenges(data.challenges || []);
        } catch {
            setChallenges([]);
        } finally {
            setChallengesLoading(false);
        }
    };

    const handleSendFriendRequest = async () => {
        if (!profile?.user_id) return;
        setActionLoading(true);
        setActionError('');
        setActionMessage('');
        try {
            await sendFriendRequest(profile.user_id);
            setActionMessage('Friend request sent.');
            setProfile(prev => ({ ...prev, friendship_status: 'pending_sent' }));
        } catch (err) {
            setActionError(err.message || 'Failed to send friend request');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAcceptRequest = async () => {
        if (!profile?.user_id) return;
        setActionLoading(true);
        setActionError('');
        setActionMessage('');
        try {
            await respondFriendRequest(profile.user_id, 'accepted');
            setActionMessage('Friend request accepted.');
            const data = await getUserProfileFull(username);
            setProfile(data);
            fetchActivities(data.username);
            fetchChallenges(data.username);
        } catch (err) {
            setActionError(err.message || 'Failed to accept friend request');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectRequest = async () => {
        if (!profile?.user_id) return;
        setActionLoading(true);
        setActionError('');
        setActionMessage('');
        try {
            await respondFriendRequest(profile.user_id, 'rejected');
            setActionMessage('Friend request declined.');
            setProfile(prev => ({ ...prev, friendship_status: 'none' }));
        } catch (err) {
            setActionError(err.message || 'Failed to decline friend request');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelRequest = async () => {
        if (!profile?.user_id) return;
        setActionLoading(true);
        setActionError('');
        setActionMessage('');
        setConfirmAction(null);
        try {
            await deleteFriendRequest(profile.user_id);
            setActionMessage('Friend request cancelled.');
            setProfile(prev => ({ ...prev, friendship_status: 'none' }));
        } catch (err) {
            setActionError(err.message || 'Failed to cancel friend request');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveFriend = async () => {
        if (!profile?.user_id) return;
        setActionLoading(true);
        setActionError('');
        setActionMessage('');
        setConfirmAction(null);
        try {
            await deleteFriend(profile.user_id);
            setActionMessage('Friend removed.');
            setProfile(prev => ({ ...prev, friendship_status: 'none', weekly_points: undefined, last_week_points: undefined }));
            setActivities([]);
            setChallenges([]);
        } catch (err) {
            setActionError(err.message || 'Failed to remove friend');
        } finally {
            setActionLoading(false);
        }
    };

    const handleBlockUser = async () => {
        if (!profile?.user_id) return;
        setActionLoading(true);
        setActionError('');
        setActionMessage('');
        setConfirmAction(null);
        try {
            await blockFriend(profile.user_id);
            setActionMessage('User blocked.');
            setProfile(prev => ({ ...prev, friendship_status: 'none', weekly_points: undefined, last_week_points: undefined }));
            setActivities([]);
            setChallenges([]);
        } catch (err) {
            setActionError(err.message || 'Failed to block user');
        } finally {
            setActionLoading(false);
        }
    };

    const renderShell = (children) => (
        <div className="sc-page up-page">
            <div className="sc-container">
                {children}
            </div>
        </div>
    );

    const renderHeader = () => (
        <header className="sc-header up-header">
            <button type="button" className="sc-back-link" onClick={() => history.goBack()}>
                ← Back
            </button>
            <h2>Profile</h2>
        </header>
    );

    if (loading) {
        return renderShell(
            <div className="sc-loading">
                <div className="sc-spinner" />
                <p>Loading profile…</p>
            </div>
        );
    }

    if (error) {
        return renderShell(
            <>
                {renderHeader()}
                <div className="up-alerts">
                    <div className="sc-alert sc-alert--error" role="alert">{error}</div>
                </div>
            </>
        );
    }

    if (!profile) return null;

    const status = profile.friendship_status;
    const isFriend = status === 'friend';
    const isSelf = status === 'self';
    const isPendingSent = status === 'pending_sent';
    const isPendingReceived = status === 'pending_received';
    const showFriendContent = isFriend || isSelf;
    const showStats = showFriendContent && profile.weekly_points !== undefined;
    const statusLabel = relationshipLabel(status);

    const confirmCopy = {
        remove: `Remove ${profile.username} as a friend?`,
        block: `Block ${profile.username}? This also removes the friendship.`,
        cancel: `Cancel your friend request to ${profile.username}?`,
    }[confirmAction];

    const renderFriendMenu = () => {
        if (!isFriend || confirmAction) return null;
        const choose = (action) => {
            setMenuOpen(false);
            setConfirmAction(action);
        };
        return (
            <div className="up-menu-group">
                <button
                    type="button"
                    className="up-menu-button up-menu-button--hot"
                    aria-label="Send Hot Potato"
                    title="Send Hot Potato"
                    onClick={() => history.push(`/hot-potato/send?to=${encodeURIComponent(profile.username)}`)}
                >
                    <HotPotatoIcon aria-hidden="true" />
                </button>
                <div className="up-menu" ref={menuRef}>
                    <button
                        type="button"
                        className="up-menu-button"
                        aria-label="Options"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((open) => !open)}
                    >
                        <MoreIcon aria-hidden="true" />
                    </button>
                    {menuOpen && (
                        <div className="up-menu-panel" role="menu" aria-label="Friend options">
                            <button
                                type="button"
                                role="menuitem"
                                className="up-menu-item"
                                onClick={() => choose('remove')}
                            >
                                Remove friend
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="up-menu-item up-menu-item--danger"
                                onClick={() => choose('block')}
                            >
                                Block
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderActions = () => {
        if (isSelf || confirmAction || isFriend) return null;

        if (isPendingSent) {
            return (
                <div className="up-actions">
                    <button
                        type="button"
                        className="sc-btn sc-btn--secondary sc-btn--sm"
                        onClick={() => setConfirmAction('cancel')}
                        disabled={actionLoading}
                    >
                        Cancel request
                    </button>
                </div>
            );
        }

        if (isPendingReceived) {
            return (
                <div className="up-actions">
                    <button
                        type="button"
                        className="sc-btn sc-btn--primary sc-btn--sm"
                        onClick={handleAcceptRequest}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Accepting…' : 'Accept'}
                    </button>
                    <button
                        type="button"
                        className="sc-btn sc-btn--danger sc-btn--sm"
                        onClick={handleRejectRequest}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Declining…' : 'Decline'}
                    </button>
                </div>
            );
        }

        if (status === 'none') {
            return (
                <div className="up-actions">
                    <button
                        type="button"
                        className="sc-btn sc-btn--primary sc-btn--sm"
                        onClick={handleSendFriendRequest}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Sending…' : 'Add friend'}
                    </button>
                </div>
            );
        }

        return null;
    };

    return renderShell(
        <>
            {renderHeader()}

            <section className="up-summary">
                <div className="up-summary-row">
                    <div className="up-summary-identity">
                        <div className="up-avatar-ring">
                            {avatarSvg && (
                                <Avatar
                                    svgContent={avatarSvg}
                                    alt={`${profile.username}'s avatar`}
                                    className="up-avatar"
                                />
                            )}
                        </div>
                        <div className="up-summary-text">
                            <h3>{profile.username}</h3>
                            {statusLabel && <p>{statusLabel}</p>}
                            {renderActions()}
                        </div>
                    </div>

                    {renderFriendMenu()}

                    {showStats && (
                        <div className="up-summary-stats" aria-label="Activity summary">
                            <div className="up-stat up-stat--points">
                                <span className="up-stat-value">{profile.weekly_points ?? 0}</span>
                                <span className="up-stat-label">SP this week</span>
                            </div>
                            <div className="up-stat">
                                <span className="up-stat-value">{profile.last_week_points ?? 0}</span>
                                <span className="up-stat-label">SP last week</span>
                            </div>
                            <div className="up-stat up-stat--streak">
                                <span className="up-stat-value">{profile.streak ?? 0}</span>
                                <span className="up-stat-label">Day streak</span>
                            </div>
                        </div>
                    )}
                </div>

                {confirmAction && (
                    <div className={`up-confirm ${confirmAction === 'cancel' ? 'up-confirm--neutral' : ''}`}>
                        <p>{confirmCopy}</p>
                        <div className="up-confirm-actions">
                            {confirmAction === 'remove' && (
                                <button type="button" className="sc-btn sc-btn--danger sc-btn--sm" onClick={handleRemoveFriend} disabled={actionLoading}>
                                    {actionLoading ? 'Removing…' : 'Remove'}
                                </button>
                            )}
                            {confirmAction === 'block' && (
                                <button type="button" className="sc-btn sc-btn--danger sc-btn--sm" onClick={handleBlockUser} disabled={actionLoading}>
                                    {actionLoading ? 'Blocking…' : 'Block'}
                                </button>
                            )}
                            {confirmAction === 'cancel' && (
                                <button type="button" className="sc-btn sc-btn--secondary sc-btn--sm" onClick={handleCancelRequest} disabled={actionLoading}>
                                    {actionLoading ? 'Cancelling…' : 'Cancel request'}
                                </button>
                            )}
                            <button type="button" className="sc-btn sc-btn--secondary sc-btn--sm" onClick={() => setConfirmAction(null)} disabled={actionLoading}>
                                {confirmAction === 'cancel' ? 'Keep request' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                )}

                {(actionMessage || actionError) && (
                    <div className="up-feedback" aria-live="polite">
                        {actionMessage && <div className="sc-alert sc-alert--success" role="status">{actionMessage}</div>}
                        {actionError && <div className="sc-alert sc-alert--error" role="alert">{actionError}</div>}
                    </div>
                )}
            </section>

            <div className="up-content">
                {!showFriendContent && (
                    <p className="up-empty">
                        Friend activity and challenges show up here once you are friends.
                    </p>
                )}

                {showFriendContent && (
                    <section className="up-section">
                        <p className="up-section-title">Challenges</p>
                        {challengesLoading ? (
                            <p className="up-empty">Loading challenges…</p>
                        ) : challenges.length === 0 ? (
                            <p className="up-empty">Not in any challenges.</p>
                        ) : (
                            <div className="up-list">
                                {challenges.map((ch) => {
                                    const { label, tone } = challengeStatus(ch);
                                    return (
                                        <button
                                            type="button"
                                            key={ch.challenge_id}
                                            className="up-card"
                                            onClick={() => history.push(`/challenges/${ch.challenge_id}`)}
                                        >
                                            <span className="up-card-body">
                                                <span className="up-card-title">
                                                    {ch.icon && <span className="up-card-icon" aria-hidden="true">{ch.icon}</span>}
                                                    {ch.name}
                                                </span>
                                                <span className="up-card-meta">
                                                    {ch.challenge_type && <span className="up-card-token">{prettyToken(ch.challenge_type)}</span>}
                                                    {ch.sp_goal != null && <span>{ch.sp_goal} SP goal</span>}
                                                </span>
                                            </span>
                                            <span className={`up-status up-status--${tone}`}>{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                )}

                {showFriendContent && (
                    <section className="up-section">
                        <p className="up-section-title">Activity</p>
                        {activitiesLoading && activities.length === 0 ? (
                            <p className="up-empty">Loading activities…</p>
                        ) : activities.length === 0 ? (
                            <p className="up-empty">No activities logged yet.</p>
                        ) : (
                            <>
                                <div className="up-list">
                                    {activities.map((activity) => (
                                        <button
                                            type="button"
                                            key={activity.id}
                                            className="up-card"
                                            onClick={() => history.push(`/activity/${activity.id}`)}
                                        >
                                            <span className="up-card-body">
                                                <span className="up-card-title">
                                                    {activity.activity_name || formatActivityName(activity.activity)}
                                                </span>
                                                <span className="up-card-meta">
                                                    <span>{new Date(activity.date + 'T00:00').toLocaleDateString()}</span>
                                                    {activity.time != null && activity.time > 0 && <span>{Math.round(activity.time)} min</span>}
                                                    {activity.personal_effort != null && (
                                                        <span className="up-effort">Effort {activity.personal_effort}/10</span>
                                                    )}
                                                </span>
                                            </span>
                                            <span className="up-points">{activity.points ?? 0} SP</span>
                                        </button>
                                    ))}
                                </div>
                                {activitiesHasMore && (
                                    <button
                                        type="button"
                                        className="sc-btn sc-btn--secondary up-load-more"
                                        onClick={() => fetchActivities(profile.username, activities.length)}
                                        disabled={activitiesLoading}
                                    >
                                        {activitiesLoading ? 'Loading…' : 'Load more'}
                                    </button>
                                )}
                            </>
                        )}
                    </section>
                )}
            </div>
        </>
    );
};

export default UserProfilePage;
